import { useEffect, useMemo, useState } from 'react';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useAuth } from '@/hooks/useAuth';
import { usePriceLabStore } from '@/store/priceLabStore';
import { supabase } from '@/integrations/supabase/client';
import { humanizePrice } from '@/lib/humanizePrice';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/lib/toast';
import { X, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/formatMoney';

interface Row {
	id: string;
	title: string;
	product_type: string | null;
	current: number; // dollars
	suggested: number;
	reason: string;
	deltaPct: number;
	changed: boolean;
	include: boolean;
}

export function HumanizePricesConsole() {
	const { user } = useAuth();
	const { isAdmin } = useIsAdmin();
	const { open, setOpen, toggle } = usePriceLabStore();
	const [loading, setLoading] = useState(false);
	const [applying, setApplying] = useState(false);
	const [rows, setRows] = useState<Row[]>([]);

	// Hotkey: Ctrl/Cmd+Shift+H — admin only
	useEffect(() => {
		if (!isAdmin) return;
		const handler = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'h') {
				e.preventDefault();
				toggle();
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [isAdmin, toggle]);

	const fetchAndCompute = async () => {
		if (!user) return;
		setLoading(true);
		try {
			const { data, error } = await supabase
				.from('user_items')
				.select('id, title, product_type, asking_price_cents')
				.eq('user_id', user.id)
				.eq('visibility', 'listed')
				.is('sold_at', null)
				.limit(2000);
			if (error) throw error;

			const computed: Row[] = (data ?? [])
				.map((it: any) => {
					const cents = it.asking_price_cents ?? (typeof it.price === 'number' ? Math.round(it.price * 100) : null);
					if (!cents || cents <= 0) return null;
					const current = cents / 100;
					const res = humanizePrice({
						price: current,
						product_type: it.product_type as any,
						title: it.title,
					});
					return {
						id: it.id,
						title: it.title ?? 'Untitled',
						product_type: it.product_type ?? null,
						current,
						suggested: res.suggested,
						reason: res.reason,
						deltaPct: res.deltaPct,
						changed: res.changed,
						include: res.changed,
					};
				})
				.filter((r): r is Row => r !== null);

			setRows(computed);
			toast.success(`Loaded ${computed.length} listed items · ${computed.filter((r) => r.changed).length} suggested changes`);
		} catch (e: any) {
			console.error('[HumanizePrices] fetch failed', e);
			toast.error(e?.message ?? 'Failed to load items');
		} finally {
			setLoading(false);
		}
	};

	const applyChanges = async () => {
		const toApply = rows.filter((r) => r.include && r.changed);
		if (toApply.length === 0) {
			toast.info('Nothing to apply');
			return;
		}
		setApplying(true);
		try {
			// Batched updates in chunks of 50
			const chunkSize = 50;
			let done = 0;
			for (let i = 0; i < toApply.length; i += chunkSize) {
				const chunk = toApply.slice(i, i + chunkSize);
				await Promise.all(
					chunk.map((r) =>
						supabase
							.from('user_items')
							.update({
								asking_price_cents: Math.round(r.suggested * 100),
							})
							.eq('id', r.id)
							.eq('user_id', user!.id)
					)
				);
				done += chunk.length;
			}
			toast.success(`Humanized ${done} prices`);
			// Refresh
			fetchAndCompute();
		} catch (e: any) {
			console.error('[HumanizePrices] apply failed', e);
			toast.error(e?.message ?? 'Apply failed');
		} finally {
			setApplying(false);
		}
	};

	const stats = useMemo(() => {
		const changed = rows.filter((r) => r.changed);
		const included = rows.filter((r) => r.include && r.changed);
		return { total: rows.length, changed: changed.length, included: included.length };
	}, [rows]);

	if (!isAdmin || !open) return null;

	return (
		<div
			className="fixed z-[9999] bottom-4 right-4 w-[min(96vw,720px)] max-h-[80vh] rounded-2xl border border-active-gold/40 shadow-2xl glass-obsidian flex flex-col overflow-hidden"
			role="dialog"
			aria-label="Humanize Prices"
		>
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
				<div className="flex items-center gap-2">
					<Sparkles className="h-4 w-4 text-active-gold" />
					<h2 className="text-sm font-semibold text-foreground">Humanize Prices</h2>
					<span className="text-[11px] text-muted-foreground uppercase tracking-wide">±2% cap</span>
				</div>
				<div className="flex items-center gap-2">
					<span className="text-[11px] text-muted-foreground hidden sm:inline">
						⌘⇧H to toggle
					</span>
					<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Body */}
			<div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
				{rows.length === 0 ? (
					<div className="text-center py-10 space-y-3">
						<p className="text-sm text-muted-foreground">
							{loading ? 'Loading your listed items…' : 'Fetch your listed inventory to preview suggested price shapes.'}
						</p>
						<Button onClick={fetchAndCompute} disabled={loading}>
							{loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
							Fetch listed items
						</Button>
					</div>
				) : (
					<>
						<div className="flex items-center gap-3 text-xs text-muted-foreground">
							<span>{stats.total} items</span>
							<span>·</span>
							<span className="text-active-gold">{stats.changed} suggested</span>
							<span>·</span>
							<span>{stats.included} selected</span>
						</div>
						<div className="rounded-lg border border-white/10 overflow-hidden">
							<table className="w-full text-xs">
								<thead className="bg-white/[0.03] text-muted-foreground">
									<tr>
										<th className="text-left px-2 py-2 w-8"></th>
										<th className="text-left px-2 py-2">Title</th>
										<th className="text-right px-2 py-2">Current</th>
										<th className="text-right px-2 py-2">Suggested</th>
										<th className="text-right px-2 py-2">Δ</th>
										<th className="text-left px-2 py-2">Reason</th>
									</tr>
								</thead>
								<tbody>
									{rows.map((r) => (
										<tr
											key={r.id}
											className={cn(
												'border-t border-white/5',
												!r.changed && 'opacity-40'
											)}
										>
											<td className="px-2 py-1.5">
												<Checkbox
													checked={r.include && r.changed}
													disabled={!r.changed}
													onCheckedChange={(v) =>
														setRows((prev) =>
															prev.map((p) => (p.id === r.id ? { ...p, include: !!v } : p))
														)
													}
												/>
											</td>
											<td className="px-2 py-1.5 max-w-[240px] truncate text-foreground">{r.title}</td>
											<td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
												{formatUsd(r.current)}
											</td>
											<td className="px-2 py-1.5 text-right tabular-nums text-active-gold font-medium">
												{formatUsd(r.suggested)}
											</td>
											<td
												className={cn(
													'px-2 py-1.5 text-right tabular-nums text-[11px]',
													r.deltaPct > 0 ? 'text-success' : r.deltaPct < 0 ? 'text-destructive' : 'text-muted-foreground'
												)}
											>
												{r.changed ? `${(r.deltaPct * 100).toFixed(2)}%` : '—'}
											</td>
											<td className="px-2 py-1.5 text-muted-foreground">{r.reason}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>

			{/* Footer */}
			{rows.length > 0 && (
				<div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-white/10">
					<Button variant="ghost" size="sm" onClick={fetchAndCompute} disabled={loading}>
						{loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
						Refresh
					</Button>
					<Button
						onClick={applyChanges}
						disabled={applying || stats.included === 0}
						className="bg-active-gold text-black hover:bg-active-gold/90"
					>
						{applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
						Apply {stats.included} change{stats.included === 1 ? '' : 's'}
					</Button>
				</div>
			)}
		</div>
	);
}
